/**
 * Browser-side file → structure. THE FILE NEVER LEAVES THE DEVICE (ADR-016).
 *
 * pdfjs needs its worker registered explicitly under Vite; the `?url` import is
 * the supported pattern and keeps the worker out of the main bundle.
 */
import * as pdfjsLib from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { extractColumns, looksLikeLinkedIn } from "../utils/pdfExtract.js";
import { parseLinkedIn, assessGaps, parseDocxText } from "../utils/resumeParser.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export async function parseResumeFile(file, resolver) {
  const buf = new Uint8Array(await file.arrayBuffer());
  const name = (file.name || "").toLowerCase();

  if (name.endsWith(".pdf")) {
    const cols = await extractColumns(pdfjsLib, buf);
    if (looksLikeLinkedIn(cols)) {
      return assessGaps(parseLinkedIn(cols, resolver));
    }
    // A PDF that is not a LinkedIn export: no reliable structure, so we fall
    // back to the whole text as task evidence and ask more (ADR-025 rung 5).
    return assessGaps({
      source: "pdf-freeform", sectionsFound: [], titles: [], roles: [],
      currentRole: null, skills: [], topSkillsCount: 0, tenureMonths: null,
      durationsSeen: 0, taskText: cols.all.join(" ").slice(0, 2000), gaps: [],
    });
  }

  if (name.endsWith(".docx")) {
    const mammoth = await import("mammoth/mammoth.browser.js");
    const { value } = await mammoth.extractRawText({ arrayBuffer: buf.buffer });
    // All of the parsing lives in resumeParser.js. It used to live HERE, with a
    // hand-copied duplicate in tests/_harness.mjs — so this file, the production
    // entry point, had no test coverage at all and a broken parser went green on
    // 55 tests (docs/TEST_STRATEGY.md G1). This function now does file I/O and
    // nothing else.
    return assessGaps(parseDocxText(value, resolver));
  }

  throw new Error("Upload a PDF or Word document");
}
